import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@atgis/ui";
import { GitBranch, Layers3, Map } from "lucide-react";
import { SectionHeading } from "../components/SectionHeading";

const PILLARS = [
  {
    icon: Layers3,
    tone: "master",
    title: "Mevcut Altyapı",
    description:
      "Şirketin gerçek dünyadaki şebekesini temsil eden ana proje. Kurumsal veri modeline bağlı, versiyonlu, denetlenebilir.",
    points: [
      "Her varlık için sürüm geçmişi ve onay zinciri",
      "Coğrafi sınır ve varlık tipi bazında yetki",
      "Altyapı Projesi'lere kaynak veri olarak eksport",
    ],
  },
  {
    icon: GitBranch,
    tone: "sub",
    title: "Altyapı Projesi",
    description:
      "Mevcut Altyapı'dan koparılan saha çalışma kopyası. Saha düzenleme yetkisi olan ekip burada çalışır, hazır olunca yaptığı değişiklikleri revizyon olarak kuruma onaya gönderir.",
    points: [
      "Mevcut Altyapı'ya yansımadan değişiklik biriktirme",
      "Onay bekleyen revizyon → kurum onayı → versiyonlu birleşme",
      "Coğrafi sınır ve varlık filtresiyle daraltılmış kapsam",
    ],
  },
  {
    icon: Map,
    tone: "plain",
    title: "Serbest Proje",
    description:
      "Kurumsal veri modeli gerektirmeyen; hızlı çizim, etüt ve özel proje çalışmaları. Bireysel kullanıcı veya workspace ekibi paylaşımıyla.",
    points: [
      "Free / basic kullanıcılar için ideal",
      "Workspace içinde ekiple paylaşılabilir",
      "Düzenleme veya görüntüleme yetkileriyle dağıtım",
    ],
  },
];

const TONE_CLASS: Record<string, { dot: string; ring: string; text: string }> = {
  master: {
    dot: "bg-[hsl(var(--project-master))]",
    ring: "ring-[hsl(var(--project-master))]/30",
    text: "text-[hsl(var(--project-master))]",
  },
  sub: {
    dot: "bg-[hsl(var(--project-sub))]",
    ring: "ring-[hsl(var(--project-sub))]/30",
    text: "text-[hsl(var(--project-sub))]",
  },
  plain: {
    dot: "bg-[hsl(var(--project-plain))]",
    ring: "ring-[hsl(var(--project-plain))]/30",
    text: "text-[hsl(var(--project-plain))]",
  },
};

export function Product() {
  return (
    <section id="urun" className="border-b border-border/60">
      <div className="container py-20 md:py-28">
        <SectionHeading
          eyebrow="Proje Mimarisi"
          title={
            <>
              Üç proje tipi, <span className="gradient-text">tek omurga</span>.
            </>
          }
          description="RADGIS, kurumsal GIS verisini master/sub mimarisiyle yönetir; sahadaki değişiklik onaylanmadan ana şebekeye işlemez. Veri bütünlüğü tasarımdan gelir."
        />

        <div className="mt-14 grid gap-6 lg:grid-cols-3">
          {PILLARS.map(({ icon: Icon, tone, title, description, points }) => {
            const t = TONE_CLASS[tone]!;
            return (
              <Card key={title} className="relative overflow-hidden">
                <div
                  className={`absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-current to-transparent ${t.text}`}
                />
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <div
                      className={`grid h-10 w-10 place-items-center rounded-lg bg-muted ring-1 ${t.ring}`}
                    >
                      <Icon className={`h-5 w-5 ${t.text}`} />
                    </div>
                    <span className="inline-flex items-center gap-1.5 rounded-full bg-muted px-2.5 py-0.5 text-[11px] font-medium uppercase tracking-wider text-muted-foreground">
                      <span className={`h-1.5 w-1.5 rounded-full ${t.dot}`} />
                      {title}
                    </span>
                  </div>
                  <CardTitle className="mt-4 text-xl">{title}</CardTitle>
                  <CardDescription className="text-base">{description}</CardDescription>
                </CardHeader>
                <CardContent>
                  <ul className="space-y-2 text-sm text-muted-foreground">
                    {points.map((p) => (
                      <li key={p} className="flex gap-2">
                        <span className={`mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full ${t.dot}`} />
                        <span>{p}</span>
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>
            );
          })}
        </div>

        <div className="mt-12 flex flex-col items-start gap-3 rounded-2xl border border-primary/30 bg-primary/5 p-6 text-sm text-muted-foreground md:flex-row md:items-center md:justify-between md:gap-6 md:text-base">
          <div>
            <span className="font-medium text-foreground">
              Altyapı Projesi'nden Mevcut Altyapı'ya değişiklik nasıl akar?
            </span>{" "}
            Saha ekibi düzenler, kurum onaylar, master versiyonlanır.
          </div>
          <a
            href="#changeset"
            className="inline-flex items-center gap-1.5 rounded-md bg-primary px-3 py-1.5 text-xs font-semibold text-primary-foreground hover:bg-primary/90 md:text-sm"
          >
            Revizyon onay akışını incele →
          </a>
        </div>
      </div>
    </section>
  );
}
